<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<!-- eslint-disable vue/max-lines-per-block -->
<script setup lang="ts">
  import { ref, watch } from 'vue'
  import type { FieldConfig, FormData } from '@/types'
  import { getNestedValue, getRangeValues } from '@/utils/formHelper'
  import DescriptionModal from './description-modal.vue'
  import CheckboxInput from './form-input/checkbox-input.vue'
  import RadioInput from './form-input/radio-input.vue'
  import RangeInput from './form-input/range-input.vue'

  const props = defineProps<{
    id: string,
    title: string,
    fields: FieldConfig[],
    formData: FormData,
    setSectionRef?: (el: HTMLElement | null) => void,
  }>()

  const sectionEl = ref<HTMLElement | null>(null)
  watch(sectionEl, (el) => props.setSectionRef?.(el), { immediate: true })

  const sliderValue = ref<Partial<Record<keyof FormData, number>>>({})
  const isModalOpen = ref(false)
  const modalDescription = ref<FieldConfig['description']>('')

  const disableConditions: Record<string, { trigger: string, affectedFields: string[] }> = {
    'website-apps': {
      trigger: 'builder',
      affectedFields: ['uniquePages', 'designPreference', 'languages', 'mobileApps', 'features'],
    },
    'design': {
      trigger: 'logo',
      affectedFields: ['logoFeatures'],
    },
  }

  function openModal (desc: FieldConfig['description']) {
    modalDescription.value = desc
    isModalOpen.value = true
  }

  function getNestedField (key: string): any {
    return getNestedValue(props.formData, key)
  }

  function updateNestedField (key: string, value: any): void {
    const parts = key.split('.')
    let cur: any = props.formData
    for (let i = 0; i < parts.length - 1; i++) {
      cur[parts[i]] = cur[parts[i]] || {}
      cur = cur[parts[i]]
    }
    cur[parts[parts.length - 1]] = value
  }

  function shouldDisableField (sectionId: string, fieldKey: string): boolean {
    const condition = disableConditions[sectionId]
    if (!condition) return false
    return condition.affectedFields.includes(fieldKey) && isFieldNo(condition.trigger)
  }

  function isFieldNo (fieldKey: string): boolean {
    return getNestedField(fieldKey) === 'no'
  }

  // Initialize slider indices & select defaults
  watch(
    () => props.fields,
    (fields) => {
      fields.forEach((field) => {
        if (field.type === 'range' && field) {
          const { values: rangeValues } = getRangeValues(field, props.formData)
          const currentValue = getNestedField(field.key)
          const idx = rangeValues.indexOf(currentValue)
          sliderValue.value[field.key as keyof FormData] = idx >= 0 ? idx : 0
        }
        if (field.type === 'select' && field.condition) {
          const condVal = getNestedField(field.condition.key)
          const currentVal = getNestedField(field.key)
          if (field.condition.values.includes(condVal) && currentVal === undefined) {
            const defaultValue = field.options?.[0]?.value
            if (defaultValue) {
              updateNestedField(field.key, defaultValue)
            }
          }
        }
      })
    },
    { immediate: true, deep: true },
  )

  watch(
    sliderValue,
    (newVals) => {
      Object.entries(newVals).forEach(([key, idx]) => {
        const field = props.fields.find((f) => f.key === key && f.type === 'range')
        if (field) {
          const { values: rangeValues } = getRangeValues(field, props.formData)
          updateNestedField(key, rangeValues[idx as number] || rangeValues[0])
        }
      })
    },
    { deep: true },
  )

  const getNextSelectField = (i: number): FieldConfig | null => {
    const next = props.fields[i + 1]
    if (
      next &&
      next.type === 'select' &&
      next.condition &&
      next.condition.values.includes(getNestedField(next.condition.key))
    ) {
      return next
    }
    return null
  }
</script>

<template>
  <section
    :id="id"
    ref="sectionEl"
    class="mb-16 min-h-[50vh]">
    <h2 class="lg:text-2xl md:text-xl text-lg text-primary-700 mb-4 font-semibold">
      {{ title }}
    </h2>

    <div class="space-y-8">
      <div
        v-for="(field, idx) in props.fields"
        v-show="field.type !== 'select' || !getNextSelectField(idx - 1)"
        :key="field.key"
        :class="{
          'opacity-50 pointer-events-none': shouldDisableField(id, field.key),
        }">
        <form-header
          :field="field"
          @open-modal="openModal" />

        <radio-input
          v-if="field.type === 'radio'"
          :field="field"
          :form-data="props.formData"
          :disabled="shouldDisableField(id, field.key)"
          @update-field="updateNestedField"
          @open-modal="openModal" />
        <checkbox-input
          v-else-if="field.type === 'checkbox'"
          :field="field"
          :form-data="props.formData"
          :disabled="shouldDisableField(id, field.key)"
          @update-field="updateNestedField"
          @open-modal="openModal" />
        <range-input
          v-else-if="field.type === 'range'"
          :field="field"
          :form-data="props.formData"
          :slider-value="sliderValue"
          :next-select-field="getNextSelectField(idx)"
          :index="idx"
          :disabled="shouldDisableField(id, field.key)"
          @update-field="updateNestedField"
          @open-modal="openModal" />
      </div>
    </div>
    <description-modal
      :is-open="isModalOpen"
      :description="modalDescription"
      @update:is-open="isModalOpen = $event" />
  </section>
</template>
